import { PreviewType } from "@components/Map/type";
import { Button, Grid, Paper, Stack } from "@mui/material";
import { revalidateLogic } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";

import { DeliveryType as ApiDeliveryType } from "@/types/models/delivery.types";

import DeliveryMap from "../components/DeliveryMap";
import { step1Schema } from "../form-config";
import { AddressSection } from "../sections/AddressSection";
import { VehicleSection } from "../sections/VehicleSection";
import type { DeliveryFormType } from "../types";
import { scrollToFirstError } from "../utils/scrollToFirstError";

const mapToApiDeliveryType = (type: string): ApiDeliveryType => {
  switch (type) {
    case "okm":
      return "OKM";
    case "to-return":
      return "TO_RETURN";
    case "two-way-delivery":
      return "TWO_WAY_DELIVERY";
    case "standard":
    default:
      return "STANDARD";
  }
};

interface Props {
  form: DeliveryFormType;
  type: string;
  onStepValid: () => void;
  onPrevious: () => void;
}

export function StepAddressVehicle({ form, type, onStepValid, onPrevious }: Props) {
  const { t } = useTranslation();

  return (
    <form.FormGroup
      name="step1"
      validators={{ onDynamic: step1Schema }}
      validationLogic={revalidateLogic()}
      onGroupSubmit={() => {
        onStepValid();
      }}
      onGroupSubmitInvalid={scrollToFirstError}
    >
      {(formGroup) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            formGroup.handleSubmit();
          }}
        >
          <Grid container spacing={4}>
            <Grid size={{ lg: 6, xs: 12 }}>
              <Stack gap={4}>
                <Paper elevation={4} sx={paperSx}>
                  <AddressSection
                    form={form}
                    title={t("delivery.form.departure-address")}
                    identifier="step1.fromAddress"
                  />
                </Paper>
                <Paper elevation={4} sx={paperSx}>
                  <AddressSection
                    form={form}
                    title={t("delivery.form.arrival-address")}
                    identifier="step1.toAddress"
                  />
                </Paper>
              </Stack>
            </Grid>

            <Grid size={{ lg: 6, xs: 12 }}>
              <Paper elevation={4} sx={paperSx}>
                <DeliveryMap
                  form={form}
                  previewType={PreviewType.AB}
                  type={mapToApiDeliveryType(type)}
                />
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper elevation={4} sx={paperSx}>
                <VehicleSection
                  form={form}
                  fieldPrefix="step1.vehicle"
                  title={t("delivery.form.liveried-vehicle")}
                />
              </Paper>
            </Grid>

            {/* Navigation buttons */}
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={1}>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Button color="secondary" fullWidth onClick={onPrevious} variant="contained">
                    {t("common.navigation.previous")}
                  </Button>
                </Grid>
                <Grid size={{ md: 6, xs: 12 }}>
                  <Button color="primary" fullWidth type="submit" variant="contained">
                    {t("common.navigation.next")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </form.FormGroup>
  );
}

const paperSx = { backgroundColor: "#E7E6F7", borderRadius: 1, padding: "20px", height: "100%" };

export default StepAddressVehicle;
